/*
  <bb-reviews-full> — sections/bb-product-reviews-full.liquid, the PDP's full
  review list: Trustpilot score, rating filter / search / sort, and a list
  grown one API page at a time by "load more".

  It extends <bb-reviews> (assets/bb-reviews.js, which must load first), so the
  request and its shared cache, the skeleton hand-off, the storefront empty
  state, the theme-editor note, the strings, the stars and the number / date
  formatting are the same code as every other review placement.

  What /apps/reviews/product-reviews actually supports decides what runs where
  (see snippets/bb-review-endpoint.liquid):
    - `page` + `limit` exist: the first request is page 1 with the section's
      "reviews per page" as limit; "load more" asks for the next page with the
      same SKUs, language, limit and rating, and appends it. Only
      data.hasMore === true shows the button — never data.total.
    - `stars` exists: the rating filter asks the API for that rating, starting
      again at page 1. Every URL is remembered by the shared cache.
    - no search and no sort: both run in the browser, over the reviews loaded
      so far and nothing else.
    - the score is data.stars / data.total — the aggregate, never recomputed.
    - `distribution` exists: { "1".."5": count } over the product's whole
      review history, so the rating breakdown is read from it and never
      counted from the reviews array, which holds only the pages loaded so far.
    - data.reviews[].media[].files[] holds the review's photos: the carousel
      and each row's thumbnails read them. No media filter exists, so "with
      media" runs in the browser over the reviews loaded so far, like search.
    - no helpful votes exist, so none are shown.
*/

(function () {
  'use strict';

  var BBReviews = window.BBReviews;
  if (!BBReviews || customElements.get('bb-reviews-full')) return;

  var kit = BBReviews.kit;

  var SEARCH_DEBOUNCE_MS = 200;

  /* Case- and accent-insensitive, so "tres doux" finds "très doux". */
  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  function timeOf(review) {
    var time = Date.parse(review.createdAt);
    return isNaN(time) ? 0 : time;
  }

  /* true or "true", under either name the app may send; anything else is not verified. */
  function isVerified(review) {
    var value = review.verified !== undefined ? review.verified : review.isVerified;
    return value === true || value === 'true';
  }

  function starsOf(review) {
    return Number(review.stars) || 0;
  }

  /*
    { 5: n, 4: n, 3: n, 2: n, 1: n } from the response's own data.distribution —
    the product's whole history, not the page loaded so far. A missing or partial
    object falls back to zeros, so every row still renders.
  */
  function ratingDistribution(distribution) {
    var counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    if (!distribution) return counts;
    Object.keys(counts).forEach(function (rating) {
      counts[rating] = Number(distribution[rating]) || 0;
    });
    return counts;
  }

  /* Thumbnails want the small file, the enlarged view the large one; any other is a fallback. */
  var THUMB_SIZES = ['360pxWide', '1024pxWide', 'original'];
  var LARGE_SIZES = ['1024pxWide', 'original', '360pxWide'];

  /* Only http(s) images: anything else in the payload is ignored, never rendered. */
  function imageUrl(file) {
    if (!file || typeof file.url !== 'string') return '';
    if (file.mimeType && String(file.mimeType).indexOf('image/') !== 0) return '';
    try {
      var url = new URL(file.url, window.location.href);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  function pickFile(files, sizes) {
    for (var i = 0; i < sizes.length; i++) {
      for (var j = 0; j < files.length; j++) {
        if (files[j].dimension === sizes[i]) return files[j].url;
      }
    }
    return files.length ? files[0].url : '';
  }

  /*
    [{ thumb, large }] for one review, one entry per attachment: unusable files
    are dropped, an attachment without any usable image is skipped, and the same
    attachment (by id, else by URL) is listed once.
  */
  var mediaCache = new WeakMap();

  function mediaOf(review) {
    if (!review || typeof review !== 'object') return [];
    if (mediaCache.has(review)) return mediaCache.get(review);

    var items = [];
    var seen = {};
    (Array.isArray(review.media) ? review.media : []).forEach(function (media) {
      if (!media || !Array.isArray(media.files)) return;

      var files = [];
      media.files.forEach(function (file) {
        var url = imageUrl(file);
        if (url) files.push({ dimension: file.dimension, url: url });
      });
      if (!files.length) return;

      var large = pickFile(files, LARGE_SIZES);
      var key = media.id ? 'id:' + media.id : 'url:' + large;
      if (seen[key]) return;
      seen[key] = true;

      items.push({ review: review, thumb: pickFile(files, THUMB_SIZES), large: large });
    });

    mediaCache.set(review, items);
    return items;
  }

  function hasMedia(review) {
    return mediaOf(review).length > 0;
  }

  /* Keys match the sort <select> options in the section. */
  var SORTERS = {
    recent: function (a, b) {
      return timeOf(b) - timeOf(a);
    },
    oldest: function (a, b) {
      return timeOf(a) - timeOf(b);
    },
    highest: function (a, b) {
      return starsOf(b) - starsOf(a) || timeOf(b) - timeOf(a);
    },
    lowest: function (a, b) {
      return starsOf(a) - starsOf(b) || timeOf(b) - timeOf(a);
    }
  };

  /* The first page's number, and the one every rating change restarts from. */
  var FIRST_PAGE = 1;

  /* Same review on two pages (one was posted between requests): keep the first. */
  function appendUnique(loaded, reviews) {
    var seen = {};
    loaded.forEach(function (review) {
      if (review.id) seen[review.id] = true;
    });
    return loaded.concat(
      reviews.filter(function (review) {
        if (!review.id) return true;
        if (seen[review.id]) return false;
        seen[review.id] = true;
        return true;
      })
    );
  }

  class BBReviewsFull extends BBReviews {
    connectedCallback() {
      if (this.initialised) return;
      super.connectedCallback();
      if (!this.inner) return;

      this.sectionEl = this.closest('[data-bb-reviews-full-section]');

      this.scoreStarsEl = this.querySelector('[data-bb-reviews-full-stars]');
      this.scoreRatingEl = this.querySelector('[data-bb-reviews-full-rating]');
      this.scoreCountEl = this.querySelector('[data-bb-reviews-full-count]');
      this.breakdownEl = this.querySelector('[data-bb-reviews-full-breakdown]');
      this.searchEl = this.querySelector('[data-bb-reviews-full-search]');
      this.ratingFilterEl = this.querySelector('[data-bb-reviews-full-rating-filter]');
      this.sortEl = this.querySelector('[data-bb-reviews-full-sort]');
      this.statusEl = this.querySelector('[data-bb-reviews-full-status]');
      this.resultsEl = this.querySelector('[data-bb-reviews-full-results]');
      this.loaderEl = this.querySelector('[data-bb-reviews-full-loader]');
      this.reviewListEl = this.querySelector('[data-bb-reviews-full-list]');
      this.moreEl = this.querySelector('[data-bb-reviews-full-more]');
      this.moreButtonEl = this.querySelector('[data-bb-reviews-full-more-button]');
      this.mediaFilterEl = this.querySelector('[data-bb-reviews-full-media-filter]');
      this.galleryEl = this.querySelector('[data-bb-reviews-full-gallery]');
      this.galleryTrackEl = this.querySelector('[data-bb-reviews-full-gallery-track]');
      this.galleryPrevEl = this.querySelector('[data-bb-reviews-full-gallery-prev]');
      this.galleryNextEl = this.querySelector('[data-bb-reviews-full-gallery-next]');

      /* "Show with media" off: no photos anywhere — filter, carousel and row thumbnails. */
      this.showMedia = this.dataset.showMedia === 'true';

      this.loaded = [];
      this.withMedia = false;
      this.failed = false;
      this.query = '';
      this.stars = '';
      this.sort = this.sortEl && SORTERS[this.sortEl.value] ? this.sortEl.value : 'recent';
      this.page = FIRST_PAGE;
      this.hasMore = false;
      this.loadingMore = false;
      this.requestToken = 0;

      /* Each review's row, built once: redraws move rows instead of rebuilding them. */
      this.rows = new WeakMap();

      /* The same for the carousel's slides, keyed by mediaOf()'s entries. */
      this.slides = new WeakMap();
      this.galleryFor = null;

      if (this.moreButtonEl && this.strings.load_more) this.moreButtonEl.textContent = this.strings.load_more;

      var self = this;

      if (this.searchEl) {
        this.searchEl.addEventListener('input', function () {
          clearTimeout(self.searchTimer);
          self.searchTimer = setTimeout(function () {
            self.query = self.searchEl.value;
            self.update();
          }, SEARCH_DEBOUNCE_MS);
        });
      }

      if (this.ratingFilterEl) {
        this.ratingFilterEl.addEventListener('change', function () {
          self.stars = self.ratingFilterEl.value;
          self.loadRating();
        });
      }

      if (this.sortEl) {
        this.sortEl.addEventListener('change', function () {
          self.sort = SORTERS[self.sortEl.value] ? self.sortEl.value : 'recent';
          self.update();
        });
      }

      if (this.moreButtonEl) {
        this.moreButtonEl.addEventListener('click', function () {
          self.loadMore();
        });
      }

      if (this.mediaFilterEl) {
        this.mediaFilterEl.addEventListener('click', function () {
          self.withMedia = !self.withMedia;
          self.mediaFilterEl.setAttribute('aria-pressed', self.withMedia ? 'true' : 'false');
          self.update();
        });
      }

      /* Slides and row thumbnails alike: one delegated listener opens the photo. */
      this.addEventListener('click', function (event) {
        var opener = event.target.closest ? event.target.closest('[data-bb-reviews-full-photo]') : null;
        if (!opener || !self.contains(opener) || !opener.bbMedia) return;
        self.openPhoto(opener.bbMedia, opener);
      });

      if (this.galleryTrackEl) {
        this.galleryTrackEl.addEventListener('scroll', function () {
          if (self.arrowFrame) return;
          self.arrowFrame = requestAnimationFrame(function () {
            self.arrowFrame = 0;
            self.updateGalleryArrows();
          });
        }, { passive: true });

        if (typeof ResizeObserver === 'function') {
          this.galleryObserver = new ResizeObserver(function () {
            self.updateGalleryArrows();
          });
          this.galleryObserver.observe(this.galleryTrackEl);
        }
      }

      if (this.galleryPrevEl) {
        this.galleryPrevEl.addEventListener('click', function () {
          self.scrollGallery(-1);
        });
      }

      if (this.galleryNextEl) {
        this.galleryNextEl.addEventListener('click', function () {
          self.scrollGallery(1);
        });
      }
    }

    disconnectedCallback() {
      clearTimeout(this.searchTimer);
      if (this.arrowFrame) cancelAnimationFrame(this.arrowFrame);
      if (this.galleryObserver) this.galleryObserver.disconnect();
      super.disconnectedCallback();
    }

    /*
      Not lazy: on the PDP this is the compact rating row's request, already on
      its way at page load. Settling early means a product without reviews has
      dropped the section long before anyone scrolls to it.
    */
    loadWhenNearViewport() {
      this.load();
    }

    /* The base class's first request: page 1, no rating. */
    requestUrl() {
      return this.pageUrl(FIRST_PAGE);
    }

    /*
      The section's endpoint already carries the SKUs, language and limit; only
      the rating and the page change between requests.
    */
    pageUrl(page) {
      var url = this.endpoint;
      if (this.stars) url += '&stars=' + encodeURIComponent(this.stars);
      return url + '&page=' + page;
    }

    /*
      The reviews themselves decide, never data.total: the aggregate counts the
      product's whole history, so it can be non-zero while this language's first
      page comes back empty, and there would be nothing to list.
    */
    isEmpty(data, reviews) {
      return !Array.isArray(reviews) || reviews.length === 0;
    }

    /* The whole section goes — heading, background and padding with it. */
    hide() {
      super.hide();
      if (!this.designMode && this.sectionEl) this.sectionEl.hidden = true;
    }

    render(data, reviews) {
      this.settleLoading();
      this.renderScore(data);
      this.renderBreakdown(data);
      this.renderAttribution(data.profileUrl);

      this.inner.hidden = false;
      this.classList.add('bb-reviews--loaded');

      this.setLoaded(reviews, data);
    }

    renderScore(data) {
      var stars = Number(data.stars) || 0;
      var total = Number(data.total) || 0;
      var rating = this.formatNumber(stars);

      if (this.scoreStarsEl) {
        this.scoreStarsEl.textContent = '';
        this.scoreStarsEl.appendChild(
          kit.buildStars(stars, this.symbolId, kit.fill(this.strings.rating_label, { rating: rating, max: kit.MAX_STARS }))
        );
      }

      if (this.scoreRatingEl) {
        this.scoreRatingEl.textContent = rating + ' ' + kit.fill(this.strings.out_of, { max: kit.MAX_STARS });
      }

      if (this.scoreCountEl && false) {
        this.scoreCountEl.textContent = kit.fill(total === 1 ? this.strings.based_on_one : this.strings.based_on, {
          count: this.formatNumber(total, 0)
        });
      }
    }

    /*
      The five rows come from data.distribution and data.total — the aggregate
      over every review this product has, never the reviews array, which holds
      only the pages "load more" has fetched so far.
    */
    renderBreakdown(data) {
      if (!this.breakdownEl) return;

      var counts = ratingDistribution(data && data.distribution);
      var total = Number(data && data.total) || 0;
      var self = this;

      this.breakdownEl.querySelectorAll('[data-bb-reviews-full-breakdown-row]').forEach(function (row) {
        var count = counts[row.getAttribute('data-bb-reviews-full-breakdown-row')] || 0;
        var percentage = total ? (count / total) * 100 : 0;
        var countText = self.formatNumber(count, 0);

        var track = row.querySelector('[data-bb-reviews-full-breakdown-track]');
        if (track) {
          track.setAttribute('aria-valuemax', String(total));
          track.setAttribute('aria-valuenow', String(count));
          track.setAttribute(
            'aria-valuetext',
            kit.fill(count === 1 ? self.strings.review_count_one : self.strings.review_count, { count: countText })
          );
        }

        var fillEl = row.querySelector('[data-bb-reviews-full-breakdown-fill]');
        if (fillEl) fillEl.style.width = percentage + '%';

        var countEl = row.querySelector('[data-bb-reviews-full-breakdown-count]');
        if (countEl) countEl.textContent = '(' + countText + ')';
      });

      this.breakdownEl.hidden = !total;
    }

    /*
      Server-side rating filter, from page 1 again. Every response replaces the
      loaded set; a stale response (the shopper changed rating again meanwhile)
      is ignored, and so is any "load more" still out for the previous rating.
    */
    loadRating() {
      var token = ++this.requestToken;
      var self = this;

      this.setLoadingMore(false);
      this.setBusy(true);

      this.fetchPage(FIRST_PAGE)
        .then(function (data) {
          if (token !== self.requestToken) return;

          self.setBusy(false);
          self.setLoaded(data.reviews, data);
        })
        .catch(function (error) {
          if (token !== self.requestToken) return;

          self.setBusy(false);
          self.loaded = [];
          self.failed = true;
          self.page = FIRST_PAGE;
          self.hasMore = false;
          self.update();
          self.warn(error);
        });
    }

    /*
      The next page for the current rating, appended to what is loaded. One
      request at a time: the button stays disabled until it settles. A failure
      keeps the loaded reviews and the button, so the shopper can try again.
    */
    loadMore() {
      if (this.loadingMore || !this.hasMore) return;

      var token = this.requestToken;
      var page = this.page + 1;
      var self = this;

      this.setLoadingMore(true);

      this.fetchPage(page)
        .then(function (data) {
          if (token !== self.requestToken) return;

          var before = self.loaded.length;
          var hadFocus = document.activeElement === self.moreButtonEl;
          self.failed = false;
          self.page = page;
          self.hasMore = data.hasMore === true;
          self.loaded = appendUnique(self.loaded, data.reviews);
          if (!self.stars) self.renderBreakdown(data);

          self.setLoadingMore(false);
          self.update();
          if (hadFocus && !self.hasMore) self.focusFirstNew(self.loaded.slice(before));
        })
        .catch(function (error) {
          if (token !== self.requestToken) return;

          self.failed = true;
          self.setLoadingMore(false);
          self.update();
          self.warn(error);
        });
    }

    /* Resolves with the response's data, or rejects on anything unusable. */
    fetchPage(page) {
      return kit.requestReviews(this.pageUrl(page)).then(function (payload) {
        var data = payload && payload.success === true ? payload.data : null;
        if (!data || !Array.isArray(data.reviews)) throw new Error('Unexpected reviews payload');
        return data;
      });
    }

    warn(error) {
      if (this.designMode && window.console && console.warn) {
        console.warn('[bb-reviews-full]', error.message || error);
      }
    }

    /* A first page — the initial one or a new rating's — replacing whatever was loaded. */
    setLoaded(reviews, data) {
      this.failed = false;
      this.loaded = appendUnique([], reviews);
      this.page = FIRST_PAGE;
      this.hasMore = !!data && data.hasMore === true;
      this.rows = new WeakMap();
      this.update();
    }

    /* The skeleton rows stand in for the list while a filter request is out. */
    setBusy(busy) {
      this.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (this.loaderEl) this.loaderEl.hidden = !busy;
      if (this.reviewListEl) this.reviewListEl.hidden = busy;

      if (busy) {
        if (this.moreEl) this.moreEl.hidden = true;
        if (this.statusEl) this.statusEl.textContent = '';
      }
    }

    /* "load more" ⇄ "loading..." on the button, which is disabled meanwhile. */
    setLoadingMore(loading) {
      this.loadingMore = loading;
      this.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (!this.moreButtonEl) return;

      this.moreButtonEl.disabled = loading;
      this.moreButtonEl.textContent = (loading ? this.strings.loading : this.strings.load_more) || this.moreButtonEl.textContent;
    }

    /* Search, media-filter and sort the loaded reviews, then redraw list, status and "load more". */
    update() {
      var query = normalise(this.query.trim());
      var withMedia = this.withMedia;

      var matches = this.loaded.filter(function (review) {
        if (withMedia && !hasMedia(review)) return false;
        return !query || normalise([review.title, review.text, review.author].join(' ')).indexOf(query) !== -1;
      });
      matches.sort(SORTERS[this.sort] || SORTERS.recent);

      this.renderList(matches);
      this.renderStatus(matches.length, !!query);
      this.renderLoadMore();

      /* Only a new loaded set changes the carousel; search, sort and "with media" leave it be. */
      if (this.galleryFor !== this.loaded) {
        this.galleryFor = this.loaded;
        this.renderGallery();
      }
    }

    /*
      Every photo of the reviews loaded so far, newest review first. It grows
      with "load more" and follows the rating filter, since both change what
      is loaded. Existing slides are moved, not rebuilt, so the scroll position
      and loaded images survive.
    */
    renderGallery() {
      if (!this.galleryEl || !this.galleryTrackEl) return;

      var media = [];
      this.loaded
        .slice()
        .sort(SORTERS.recent)
        .forEach(function (review) {
          media = media.concat(mediaOf(review));
        });

      var fragment = document.createDocumentFragment();
      for (var i = 0; i < media.length; i++) {
        var slide = this.slides.get(media[i]);
        if (!slide) {
          slide = this.buildSlide(media[i]);
          this.slides.set(media[i], slide);
        }
        fragment.appendChild(slide);
      }

      this.galleryTrackEl.textContent = '';
      this.galleryTrackEl.appendChild(fragment);
      this.galleryEl.hidden = !media.length;
      this.updateGalleryArrows();
    }

    buildSlide(media) {
      var item = document.createElement('li');
      item.className = 'bb-reviews-full__gallery-item';
      item.appendChild(this.buildPhotoButton(media, 'bb-reviews-full__gallery-photo'));
      return item;
    }

    /* A button around the thumbnail; the click is handled by the delegated listener. */
    buildPhotoButton(media, className) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = className;
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('data-bb-reviews-full-photo', '');
      button.bbMedia = media;

      var img = document.createElement('img');
      img.src = media.thumb;
      img.alt = this.photoAlt(media.review);
      img.width = 350;
      img.height = 500;
      img.loading = 'lazy';
      img.decoding = 'async';
      button.appendChild(img);

      return button;
    }

    photoAlt(review) {
      var name = this.showAuthor && review.author ? String(review.author).trim() : '';
      if (name && this.strings.photo_alt_by) return kit.fill(this.strings.photo_alt_by, { author: name });
      return this.strings.photo_alt || '';
    }

    /* One "page" of the carousel per click: as many slides as are fully in view. */
    scrollGallery(direction) {
      var track = this.galleryTrackEl;
      if (!track) return;

      var slide = track.firstElementChild;
      var step = track.clientWidth;
      if (slide) {
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var slideWidth = slide.getBoundingClientRect().width + gap;
        step = Math.max(1, Math.floor((track.clientWidth + gap) / slideWidth)) * slideWidth;
      }

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: direction * step, behavior: reduce ? 'auto' : 'smooth' });
    }

    /* Arrows only when the slides overflow, each disabled at its own end. */
    updateGalleryArrows() {
      var track = this.galleryTrackEl;
      if (!track || !this.galleryPrevEl || !this.galleryNextEl) return;

      var max = track.scrollWidth - track.clientWidth;
      var overflows = max > 1;
      /* RTL scrollLeft runs from 0 down to -max. */
      var position = Math.abs(track.scrollLeft);

      this.galleryPrevEl.hidden = !overflows;
      this.galleryNextEl.hidden = !overflows;
      this.galleryPrevEl.disabled = position <= 1;
      this.galleryNextEl.disabled = position >= max - 1;
    }

    /*
      The large file, with the review it came from beneath it, in the theme's
      <modal-dialog>. A review with several photos gets thumbnails to switch.
    */
    openPhoto(media, opener) {
      var modal = this.ensureModal();
      if (!modal || typeof modal.show !== 'function' || !this.modalBody) return;

      var review = media.review;
      var all = mediaOf(review);
      var body = this.modalBody;
      body.textContent = '';

      var figure = document.createElement('div');
      figure.className = 'bb-reviews-modal__photo';
      var img = document.createElement('img');
      img.alt = this.photoAlt(review);
      img.decoding = 'async';
      figure.appendChild(img);
      body.appendChild(figure);

      var thumbs = [];
      var show = function (index) {
        img.src = all[index].large;
        thumbs.forEach(function (thumb, i) {
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      };

      if (all.length > 1) {
        var list = document.createElement('ul');
        list.className = 'bb-reviews-modal__thumbs';
        var self = this;
        all.forEach(function (item, index) {
          var li = document.createElement('li');
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'bb-reviews-modal__thumb';
          button.setAttribute(
            'aria-label',
            kit.fill(self.strings.show_photo || '', { index: index + 1, total: all.length })
          );
          var thumb = document.createElement('img');
          thumb.src = item.thumb;
          thumb.alt = '';
          thumb.width = 350;
          thumb.height = 500;
          button.appendChild(thumb);
          button.addEventListener('click', function () {
            show(index);
          });
          li.appendChild(button);
          list.appendChild(li);
          thumbs.push(button);
        });
        body.appendChild(list);
      }

      show(Math.max(0, all.indexOf(media)));

      var details = this.buildReviewDetails(body, review, 'bb-reviews-modal', 'h2');
      if (this.modalDialog) {
        if (details.title) {
          details.title.id = modal.id + '-title';
          this.modalDialog.removeAttribute('aria-label');
          this.modalDialog.setAttribute('aria-labelledby', details.title.id);
        } else {
          this.modalDialog.removeAttribute('aria-labelledby');
          this.modalDialog.setAttribute('aria-label', img.alt || details.ratingLabel);
        }
      }

      modal.show(opener);
      if (this.modalClose) this.modalClose.focus();
    }

    /*
      Rows already on screen are moved, never rebuilt, so "load more" only adds
      the new ones — at the end, in the default most-recent order.
    */
    renderList(reviews) {
      if (!this.reviewListEl) return;

      var fragment = document.createDocumentFragment();
      for (var i = 0; i < reviews.length; i++) {
        var row = this.rows.get(reviews[i]);
        if (!row) {
          row = this.buildRow(reviews[i]);
          this.rows.set(reviews[i], row);
        }
        fragment.appendChild(row);
      }

      this.reviewListEl.textContent = '';
      this.reviewListEl.appendChild(fragment);
      this.reviewListEl.hidden = !reviews.length;
    }

    /* Only data.hasMore decides: shown while the API has another page. */
    renderLoadMore() {
      if (!this.moreEl) return;
      this.moreEl.hidden = !this.hasMore;
      if (this.moreButtonEl && !this.loadingMore) this.moreButtonEl.disabled = !this.hasMore;
    }

    /*
      When the focused button disappears with the last page, focus lands on the
      first new review instead of the document. While more remain, it stays on
      the button so it can be pressed again.
    */
    focusFirstNew(added) {
      for (var i = 0; i < added.length; i++) {
        var row = this.rows.get(added[i]);
        if (row && row.isConnected) {
          row.setAttribute('tabindex', '-1');
          row.focus({ preventScroll: true });
          return;
        }
      }

      if (this.resultsEl) this.resultsEl.focus({ preventScroll: true });
    }

    /* Announced through the role="status" element; empty when there is nothing to say. */
    renderStatus(count, searching) {
      if (!this.statusEl) return;

      var text = '';
      if (this.failed) {
        text = this.strings.load_error;
      } else if (!this.loaded.length) {
        text = this.stars ? this.strings.no_results : this.strings.no_reviews;
      } else if (!count) {
        text = this.withMedia && !searching && this.strings.no_media ? this.strings.no_media : this.strings.no_results;
      } else if (searching || this.stars || this.withMedia) {
        text = kit.fill(count === 1 ? this.strings.results_one : this.strings.results, {
          count: this.formatNumber(count, 0)
        });
      }

      this.statusEl.textContent = text || '';
    }

    /*
      author | stars, title, text | date

      Only fields the API returns: no avatar image, product metadata or helpful
      votes exist, so the initial stands in for the avatar. The review's photos
      follow its text, each opening the enlarged view.
    */
    buildRow(review) {
      var item = document.createElement('li');
      item.className = 'bb-reviews-full__review';

      var author = document.createElement('div');
      author.className = 'bb-reviews-full__author';

      var name = this.showAuthor && review.author ? String(review.author).trim() : '';
      if (name) {
        var avatar = document.createElement('span');
        avatar.className = 'bb-reviews-full__avatar';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = Array.from(name)[0].toLocaleUpperCase(this.locale);
        author.appendChild(avatar);

        var nameEl = document.createElement('p');
        nameEl.className = 'bb-reviews-full__name';
        nameEl.textContent = name;
        author.appendChild(nameEl);
      }


       console.log('this.showVerified');
      console.log('this.showVerified', this.showVerified, 'isVerified(review)', isVerified(review), 'this.strings.verified_buyer', this.strings.verified_buyer );
      if (this.showVerified && isVerified(review) && this.strings.verified_buyer) {
        var verified = document.createElement('p');
        verified.className = 'bb-reviews-full__verified';
        verified.textContent = this.strings.verified_buyer;
        author.appendChild(verified);
      }

      item.appendChild(author);

      var body = document.createElement('div');
      body.className = 'bb-reviews-full__body';

      var stars = starsOf(review);
      body.appendChild(
        kit.buildStars(
          stars,
          this.symbolId,
          kit.fill(this.strings.rating_label, { rating: this.formatNumber(stars), max: kit.MAX_STARS })
        )
      );

      if (review.title) {
        var title = document.createElement('h3');
        title.className = 'bb-reviews-full__title';
        title.textContent = review.title;
        body.appendChild(title);
      }

      if (review.text) {
        var text = document.createElement('p');
        text.className = 'bb-reviews-full__text';
        text.textContent = review.text;
        body.appendChild(text);
      }

      var media = this.showMedia ? mediaOf(review) : [];
      if (media.length) {
        var mediaList = document.createElement('ul');
        mediaList.className = 'bb-reviews-full__media';
        for (var i = 0; i < media.length; i++) {
          var mediaItem = document.createElement('li');
          mediaItem.appendChild(this.buildPhotoButton(media[i], 'bb-reviews-full__media-photo'));
          mediaList.appendChild(mediaItem);
        }
        body.appendChild(mediaList);
      }

      item.appendChild(body);

      var shortDate = this.formatShortDate(review.createdAt);
      if (shortDate) {
        var time = document.createElement('time');
        time.className = 'bb-reviews-full__date';
        time.setAttribute('datetime', review.createdAt);
        time.setAttribute('title', this.formatDate(review.createdAt));
        time.textContent = shortDate;
        item.appendChild(time);
      }

      return item;
    }

    /* 08/07/26, in the shopper's locale order; the long date stays on hover. */
    formatShortDate(value) {
      if (!value) return '';
      var date = new Date(value);
      if (isNaN(date.getTime())) return '';
      try {
        return date.toLocaleDateString(this.locale, { year: '2-digit', month: '2-digit', day: '2-digit' });
      } catch (error) {
        return date.toISOString().slice(0, 10);
      }
    }
  }

  customElements.define('bb-reviews-full', BBReviewsFull);
})();
